"use client"

/**
 * Listing-drafts data layer — the sanctioned supabase/builder surface for the
 * creation flow's quit guard and the My Listings "Drafts" section.
 *
 *  - `useListingDraftActions()` — save / discard / row-only delete, NO list
 *    fetch. Mounted by the creation-flow container, which must not trigger a
 *    drafts query every time the flow opens.
 *  - `useListingDrafts(userId)` — the drafts list as an FSM, plus a discard
 *    that prunes the list. Mounted by My Listings.
 *  - `useListingDraft(draftId)` — single-draft fetch+parse for the resume
 *    gate in global-creation-modal.
 *
 * Photo upload happens HERE at save time (client-side, same INSERT storage
 * policy and same `listings/…` `StoragePath` namespace as publish — so
 * publish-from-draft reuses the paths verbatim, zero file moves). Row+file
 * deletion goes through the delete route (service-role file removal);
 * `deleteDraftRowOnly` is the ONE post-publish path and never touches files —
 * they belong to the published listing at that point.
 *
 * @see lib/listings/sell-draft.ts — pure serialize/parse/meaningful
 * @see lib/listings/listing-draft-api.ts — wire builders
 */

import { useCallback, useEffect, useState } from "react"

import {
  asRawImageRef,
  parseListingCategory,
  rawImageRefValue,
  type ListingCategory,
  type RawImageRef,
  type SellDraftPhoto,
} from "@kraahl/contract"

import {
  getNormalizedImageFile,
  getSafeImageExtension,
  uploadWithConcurrency,
} from "@/lib/image-upload"
import {
  deleteListingDraft,
  ListingDraftError,
  saveListingDraft,
} from "@/lib/listings/listing-draft-api"
import { parseListingDraft, serializeListingDraft } from "@/lib/listings/sell-draft"
import { storageBucket, StoragePath } from "@/lib/storage"
import { resolveStorageUrl } from "@/lib/storage/public-url"
import { isPersistedImageRef } from "@/lib/storage/url"
import type { ListingData } from "@/types/listing"
import { createClient } from "@/utils/supabase/client"

const MAX_PHOTO_SIZE = 10 * 1024 * 1024

export type ListingDraftListItem =
  | {
      kind: "readable"
      id: string
      title: string
      listingCategory: ListingCategory | null
      updatedAt: string
      thumbnailPath: RawImageRef | null
      data: ListingData
      step: number
      imagePaths: RawImageRef[]
    }
  | {
      kind: "unreadable"
      id: string
      title: string
      listingCategory: ListingCategory | null
      updatedAt: string
      thumbnailPath: RawImageRef | null
    }

export type ListingDraftsState =
  | { kind: "loading" }
  | { kind: "error"; message: string }
  | { kind: "ready"; drafts: ListingDraftListItem[] }

export interface SaveDraftInput {
  data: ListingData
  step: number
  /** Present when re-saving a resumed draft — upserts onto the same row. */
  draftId?: string
}

interface DraftRow {
  id: string
  title: string
  listing_category: string | null
  updated_at: string
  payload: unknown
  image_paths: string[] | null
}

const DRAFT_ROW_SELECT = "id, title, listing_category, updated_at, payload, image_paths"

function toListItem(row: DraftRow): ListingDraftListItem {
  const base = {
    id: row.id,
    title: row.title,
    listingCategory: parseListingCategory(row.listing_category),
    updatedAt: row.updated_at,
    thumbnailPath: row.image_paths?.[0] ? asRawImageRef(row.image_paths[0]) : null,
  }
  const parsed = parseListingDraft(row.payload)
  if (parsed.kind === "unreadable") return { kind: "unreadable", ...base }
  return {
    kind: "readable",
    ...base,
    data: parsed.data,
    step: parsed.step,
    imagePaths: parsed.imagePaths,
  }
}

/**
 * Upload the form's effective photos for persistence. Mirrors the review
 * steps' publish loop: new `File`s upload (normalized, size-gated), already
 * persisted previews (resumed draft) pass through, `blob:` previews without a
 * file (edited slides) are re-fetched and uploaded. A photo whose upload
 * fails is dropped with a warning; if EVERY photo fails while the form has
 * some, the save aborts — a draft silently stripped of all its photos would
 * betray the "resume elsewhere" promise.
 */
async function uploadDraftPhotos(data: ListingData): Promise<SellDraftPhoto[]> {
  const source =
    data.editedPhotos && data.editedPhotos.length > 0 ? data.editedPhotos : data.photos
  if (source.length === 0) return []

  const supabase = createClient()
  const titleForPath = data.title.trim() || "draft"

  const tasks = source.map((photo, i) => async (): Promise<SellDraftPhoto | null> => {
    if (photo?.file instanceof File) {
      if (photo.file.size > MAX_PHOTO_SIZE) return null
      try {
        let fileToUpload = photo.file
        try {
          const normalized = await getNormalizedImageFile(photo.file)
          fileToUpload = normalized.file
        } catch {
          /* use original */
        }
        const ext = getSafeImageExtension(fileToUpload)
        const path = StoragePath.listing(titleForPath, i, ext)
        const { error } = await storageBucket(supabase).upload(path, fileToUpload)
        if (error) return null
        return { path: asRawImageRef(path), photoId: photo.photoId }
      } catch {
        return null
      }
    }
    if (photo?.preview && isPersistedImageRef(photo.preview)) {
      return { path: photo.preview, photoId: photo.photoId }
    }
    if (rawImageRefValue(photo?.preview)?.startsWith("blob:")) {
      try {
        const resp = await fetch(resolveStorageUrl(photo.preview))
        if (!resp.ok) return null
        const blob = await resp.blob()
        const file = new File([blob], `blob-${Date.now()}-${i}.jpg`, {
          type: blob.type || "image/jpeg",
        })
        const ext = getSafeImageExtension(file)
        const path = StoragePath.listing(titleForPath, i, ext)
        const { error } = await storageBucket(supabase).upload(path, file)
        if (error) return null
        return { path: asRawImageRef(path), photoId: photo.photoId }
      } catch {
        return null
      }
    }
    return null
  })

  const results = await uploadWithConcurrency(tasks, 3)
  const uploaded = results.filter((p): p is SellDraftPhoto => p !== null)

  if (uploaded.length === 0 && source.length > 0) {
    throw new ListingDraftError("Draft photo upload failed", null, "PHOTO_UPLOAD_FAILED")
  }
  if (uploaded.length < source.length) {
    console.warn(
      `[use-listing-drafts] ${source.length - uploaded.length}/${source.length} photo(s) dropped from draft (upload failed)`,
    )
  }
  return uploaded
}

export interface ListingDraftActions {
  /** Uploads pending photos, serializes, and creates/updates the row. */
  saveDraft(input: SaveDraftInput): Promise<{ draftId: string }>
  /** Deletes the row AND its storage files (delete route, publish-safe). */
  discardDraft(draftId: string): Promise<void>
  /**
   * Row-only delete — the ONE sanctioned post-publish cleanup. Never touches
   * storage: the files are the published listing's images now.
   */
  deleteDraftRowOnly(draftId: string): Promise<void>
}

export function useListingDraftActions(): ListingDraftActions {
  const saveDraft = useCallback(async ({ data, step, draftId }: SaveDraftInput) => {
    const photos = await uploadDraftPhotos(data)
    const payload = serializeListingDraft(data, photos, step, new Date())
    const result = await saveListingDraft({
      draftId,
      payload,
      imagePaths: photos.map((p) => p.path),
      title: data.title.trim(),
      listingCategory: data.listingCategory,
    })
    return { draftId: result.draftId }
  }, [])

  const discardDraft = useCallback(async (draftId: string) => {
    await deleteListingDraft({ draftId })
  }, [])

  const deleteDraftRowOnly = useCallback(async (draftId: string) => {
    const supabase = createClient()
    const { error } = await supabase.from("listing_drafts").delete().eq("id", draftId)
    if (error) {
      console.error("[use-listing-drafts] post-publish row delete failed", error)
      throw new ListingDraftError(error.message, null, null)
    }
  }, [])

  return { saveDraft, discardDraft, deleteDraftRowOnly }
}

export function useListingDrafts(userId: string | null | undefined) {
  const [state, setState] = useState<ListingDraftsState>({ kind: "loading" })
  const [refreshKey, setRefreshKey] = useState(0)
  const actions = useListingDraftActions()

  const refresh = useCallback(() => setRefreshKey((k) => k + 1), [])

  useEffect(() => {
    if (!userId) {
      setState({ kind: "ready", drafts: [] })
      return
    }
    let cancelled = false
    setState({ kind: "loading" })
    const supabase = createClient()
    supabase
      .from("listing_drafts")
      .select(DRAFT_ROW_SELECT)
      .eq("user_id", userId)
      .order("updated_at", { ascending: false })
      .limit(50) // deliberate bound (cap is 20) — max_rows convention
      .then(({ data, error }) => {
        if (cancelled) return
        if (error) {
          setState({ kind: "error", message: error.message })
          return
        }
        setState({
          kind: "ready",
          drafts: ((data ?? []) as DraftRow[]).map(toListItem),
        })
      })
    return () => {
      cancelled = true
    }
  }, [userId, refreshKey])

  const discardDraft = useCallback(
    async (draftId: string) => {
      await actions.discardDraft(draftId)
      setState((prev) =>
        prev.kind === "ready"
          ? { kind: "ready", drafts: prev.drafts.filter((d) => d.id !== draftId) }
          : prev,
      )
    },
    [actions],
  )

  return {
    state,
    refresh,
    saveDraft: actions.saveDraft,
    discardDraft,
    deleteDraftRowOnly: actions.deleteDraftRowOnly,
  }
}

export type ListingDraftResumeState =
  | { kind: "loading" }
  | { kind: "error" }
  | { kind: "ready"; data: ListingData; step: number; imagePaths: RawImageRef[] }

/**
 * Single-draft fetch for the resume gate. `error` covers missing row
 * (deleted on another device), fetch failure, AND unreadable payload — the
 * gate's only move in all three cases is a toast + close.
 */
export function useListingDraft(draftId: string | null): ListingDraftResumeState {
  const [state, setState] = useState<ListingDraftResumeState>({ kind: "loading" })

  useEffect(() => {
    if (!draftId) {
      setState({ kind: "loading" })
      return
    }
    let cancelled = false
    setState({ kind: "loading" })
    const supabase = createClient()
    supabase
      .from("listing_drafts")
      .select("payload")
      .eq("id", draftId)
      .maybeSingle()
      .then(({ data, error }) => {
        if (cancelled) return
        if (error || !data) {
          setState({ kind: "error" })
          return
        }
        const parsed = parseListingDraft((data as { payload: unknown }).payload)
        if (parsed.kind === "unreadable") {
          setState({ kind: "error" })
          return
        }
        setState({
          kind: "ready",
          data: parsed.data,
          step: parsed.step,
          imagePaths: parsed.imagePaths,
        })
      })
    return () => {
      cancelled = true
    }
  }, [draftId])

  return state
}
